import { Component } from '@angular/core';
import { Router, ActivatedRoute, NavigationEnd } from '@angular/router';
import { Guid } from 'guid-typescript';
import { IdentityWebApiClient, ListCompaniesByUserIdResponse, ListCompaniesByUserIdRequest, Company, CompanyStatus } from 'identity-webapi-client';
import { CookieService } from 'ngx-cookie-service';
import { filter } from 'rxjs/operators';
import { AuthService, BannerService, BannerServiceStatus, NotificationService } from './services';

@Component({
  selector: 'app-root',
  templateUrl: './app.component.html',
  styleUrls: ['./app.component.css']
})
export class AppComponent {
  private readonly cookieName = 'SituPortal.AppComponent';
  title = 'SituPortal';
  fullWidth = false;
  external = false;

  public companyIsApprovedId = Guid.parse('6FAB2318-1370-49AA-A073-7AFC10F71008');

  constructor(
    route: ActivatedRoute,
    private router: Router,
    private identityWebApiClient: IdentityWebApiClient,
    private authService: AuthService,
    private cookieService: CookieService,
    private bannerService: BannerService,
    private notificationService: NotificationService) {
    this.router.events
      .pipe(filter(e => e instanceof NavigationEnd))
      .forEach(() => {
        this.fullWidth = route.root.firstChild?.snapshot.data.fullWidth;
        this.external = route.root.firstChild?.snapshot.data.external;
      });

    authService.userSubject.subscribe(() => {
      if (!this.authService.isLoggedIn()) {
        return;
      }

      let companies = this.authService.listCompanies();
      if (!companies) {
        this.refreshAuth();
        return;
      }

      this.testAuth();
      //this.setNotifications(companies);
    });
  }

  setNotifications(companies: Company[]) {
    const companyFromClaim = this.authService.getDefaultCompany();
    // Check the claim for an approved status. If it's not approved then
    // it's possible that the claim is old and needs refreshing, so go
    // ahead and call the API directly to make sure that it's really not
    // approved before displaying the message.
    if ((companyFromClaim?.status !== CompanyStatus.Approved)) {
      this.identityWebApiClient.getCompanyById(companyFromClaim?.id ?? "")
        .then(response => {
          if ((response.company?.status !== CompanyStatus.Approved)) {
            this.bannerService.AddMessage(
              `While we review your details and your access is limited, now is a good time to read our <a href="/docs/situview/getting-started">getting started guide</a>.`,
              BannerServiceStatus.info,
              this.companyIsApprovedId);
          }
        });
    }

    this.notificationService.updateImportedProductsNotification();
  }

  testAuth(): Promise<ListCompaniesByUserIdResponse> {
    let companyId = this.authService.getDefaultCompanyId();
    if (companyId) {
      return this.identityWebApiClient.listCompaniesByUserId({ userId: this.authService.getUserId() } as ListCompaniesByUserIdRequest)
        .then(() => {
          this.clearRefreshAuthCookie();
        }, (error) => {
          if (error?.status === 401 && !this.hasRefreshedAuth()) {
            this.refreshAuth();
          }
          return error;
        });
    }

    return new Promise((resolve) => {
      resolve(new ListCompaniesByUserIdResponse());
    });
  }

  hasRefreshedAuth() {
    const cookieValue = this.cookieService.get(this.cookieName);
    let hasRefreshedAuth = false;

    if (cookieValue) {
      hasRefreshedAuth = JSON.parse(cookieValue);
    }

    return hasRefreshedAuth;
  }

  refreshAuth() {
    this.cookieService.set(this.cookieName, JSON.stringify(true));
    this.authService.logout().then(() => { this.authService.startAuthentication(this.router.url); });
  }

  clearRefreshAuthCookie() {
    this.cookieService.delete(this.cookieName);
  }
}

